import Dexie, { liveQuery, type EntityTable } from 'dexie'
import type { Category, EntityKind, OutboxItem, RemoteChange, Setting, Task } from './types'

class TasksDatabase extends Dexie {
  categories!: EntityTable<Category, 'id'>
  tasks!: EntityTable<Task, 'id'>
  outbox!: EntityTable<OutboxItem, 'id'>
  settings!: EntityTable<Setting, 'key'>

  constructor() {
    super('my-tasks')
    this.version(1).stores({
      categories: 'id, parentId, sortOrder, updatedAt, deletedAt',
      tasks: 'id, categoryId, completed, sortOrder, updatedAt, deletedAt',
      outbox: '++id, [entity+entityId], entity, entityId, createdAt',
      settings: 'key',
    })
  }
}

export const db = new TasksDatabase()

const now = () => new Date().toISOString()

async function queue(entity: EntityKind, payload: Category | Task) {
  await db.outbox.where('[entity+entityId]').equals([entity, payload.id]).delete()
  await db.outbox.add({ entity, entityId: payload.id, payload, createdAt: now() })
}

export async function initialiseDatabase() {
  if ((await db.settings.get('initialised'))?.value) return

  await db.transaction('rw', db.categories, db.outbox, db.settings, async () => {
    if ((await db.categories.count()) === 0) {
      const stamp = now()
      const seeds: Category[] = [
        { id: crypto.randomUUID(), parentId: null, name: 'Inbox', colour: '#2f7cf6', sortOrder: 0, updatedAt: stamp, deletedAt: null },
        { id: crypto.randomUUID(), parentId: null, name: 'Work', colour: '#7c5cff', sortOrder: 1, updatedAt: stamp, deletedAt: null },
        { id: crypto.randomUUID(), parentId: null, name: 'Home', colour: '#15a085', sortOrder: 2, updatedAt: stamp, deletedAt: null },
      ]
      await db.categories.bulkPut(seeds)
      for (const category of seeds) await queue('category', category)
    }
    await db.settings.put({ key: 'initialised', value: true })
  })
}

export const taskDataQuery = () =>
  liveQuery(async () => ({
    categories: await db.categories.filter((item) => !item.deletedAt).toArray(),
    tasks: await db.tasks.filter((item) => !item.deletedAt).toArray(),
  }))

export async function addCategory(name: string, parentId: string | null, colour: string) {
  const siblings = (await db.categories.toArray()).filter((item) => item.parentId === parentId && !item.deletedAt)
  const category: Category = {
    id: crypto.randomUUID(),
    parentId,
    name: name.trim(),
    colour,
    sortOrder: siblings.length,
    updatedAt: now(),
    deletedAt: null,
  }
  await db.transaction('rw', db.categories, db.outbox, async () => {
    await db.categories.put(category)
    await queue('category', category)
  })
  return category
}

export async function updateCategory(category: Category, changes: Partial<Pick<Category, 'name' | 'colour'>>) {
  const updated = { ...category, ...changes, name: changes.name?.trim() || category.name, updatedAt: now() }
  await db.transaction('rw', db.categories, db.outbox, async () => {
    await db.categories.put(updated)
    await queue('category', updated)
  })
}

export async function moveCategory(category: Category, direction: -1 | 1) {
  const siblings = (await db.categories.toArray())
    .filter((item) => item.parentId === category.parentId && !item.deletedAt)
    .sort((a, b) => a.sortOrder - b.sortOrder)
  const index = siblings.findIndex((item) => item.id === category.id)
  const swap = siblings[index + direction]
  if (index < 0 || !swap) return

  const first = { ...category, sortOrder: swap.sortOrder, updatedAt: now() }
  const second = { ...swap, sortOrder: category.sortOrder, updatedAt: now() }
  await db.transaction('rw', db.categories, db.outbox, async () => {
    await db.categories.bulkPut([first, second])
    await queue('category', first)
    await queue('category', second)
  })
}

export async function removeCategory(category: Category) {
  const stamp = now()
  await db.transaction('rw', db.categories, db.tasks, db.outbox, async () => {
    const childCategories = await db.categories.where('parentId').equals(category.id).toArray()
    const childTasks = await db.tasks.where('categoryId').equals(category.id).toArray()
    for (const child of childCategories) {
      const updated = { ...child, parentId: category.parentId, updatedAt: stamp }
      await db.categories.put(updated)
      await queue('category', updated)
    }
    const fallback = category.parentId ?? (await db.categories.filter((item) => item.id !== category.id && !item.deletedAt).first())?.id
    for (const task of childTasks) {
      const updated = fallback
        ? { ...task, categoryId: fallback, updatedAt: stamp }
        : { ...task, deletedAt: stamp, updatedAt: stamp }
      await db.tasks.put(updated)
      await queue('task', updated)
    }
    const removed = { ...category, deletedAt: stamp, updatedAt: stamp }
    await db.categories.put(removed)
    await queue('category', removed)
  })
}

export async function addTask(categoryId: string, title: string, details: string) {
  const sortOrder = await db.tasks.where('categoryId').equals(categoryId).count()
  const task: Task = {
    id: crypto.randomUUID(),
    categoryId,
    title: title.trim(),
    details: details.trim(),
    completed: false,
    sortOrder,
    updatedAt: now(),
    deletedAt: null,
  }
  await db.transaction('rw', db.tasks, db.outbox, async () => {
    await db.tasks.put(task)
    await queue('task', task)
  })
  return task
}

export async function updateTask(task: Task, changes: Partial<Pick<Task, 'title' | 'details' | 'categoryId' | 'completed'>>) {
  const updated = { ...task, ...changes, title: changes.title?.trim() || task.title, updatedAt: now() }
  await db.transaction('rw', db.tasks, db.outbox, async () => {
    await db.tasks.put(updated)
    await queue('task', updated)
  })
}

export async function removeTask(task: Task) {
  const removed = { ...task, deletedAt: now(), updatedAt: now() }
  await db.transaction('rw', db.tasks, db.outbox, async () => {
    await db.tasks.put(removed)
    await queue('task', removed)
  })
}

export async function getSetting<T>(key: string, fallback: T): Promise<T> {
  return ((await db.settings.get(key))?.value as T | undefined) ?? fallback
}

export async function setSetting<T>(key: string, value: T) {
  await db.settings.put({ key, value })
}

export async function applyRemoteChanges(changes: RemoteChange[]) {
  const pending = new Set((await db.outbox.toArray()).map((item) => `${item.entity}:${item.entityId}`))
  await db.transaction('rw', db.categories, db.tasks, async () => {
    for (const change of changes) {
      if (pending.has(`${change.entity}:${change.payload.id}`)) continue
      if (change.entity === 'category') await db.categories.put(change.payload as Category)
      else await db.tasks.put(change.payload as Task)
    }
  })
}
